import { createFileRoute } from "@tanstack/react-router"
import { CODE_PUBLIC } from "@anysola/contracts/web/launch"
import { ArrowRight, ArrowUpRight, Plus } from "lucide-react"

import { pageHead } from "@/lib/metadata"
import { notFoundHead, respondNotFound } from "@/lib/not-found"
import { PageNotFound } from "@/components/feedback/page-not-found"
import Link from "@/components/link"
import { InstallBlock } from "@/components/code/install-block"
import { BlockHead } from "@/components/layout/block-head"
import { Container } from "@/components/layout/container"
import { SiteShell } from "@/components/layout/site-shell"
import { TwoToneHeading } from "@/components/layout/two-tone-heading"
import { ACCOUNT_URL } from "@/lib/app-links"
import { summaryResetClass } from "@/lib/ui-classes"
import { cn } from "@anysola/ui/utils"

const DEVICE_URL = `${ACCOUNT_URL}/device`
const APPS_URL = `${ACCOUNT_URL}/apps`
const REPO_URL = "https://github.com/Anysola/code"
const ACTION_URL = "https://github.com/Anysola/code-action"

const description =
  "A coding agent for your terminal that signs in with your Anysola account."

const inlineLink =
  "inline-flex items-center gap-1 text-foreground underline decoration-border underline-offset-4 transition-colors hover:decoration-foreground"

const steps = [
  {
    title: "Sign in with a code",
    body: (
      <>
        On first run, Anysola Code prints a code. Open{" "}
        <a href={DEVICE_URL} className={inlineLink}>
          account.anysola.com/device
        </a>
        , type it and allow the device.
      </>
    ),
  },
  {
    title: "Usage comes from your credits",
    body: (
      <>
        On Pro and Team, each request is charged to your monthly credits, the
        same balance Chat uses.{" "}
        <Link href="/pricing" className={inlineLink}>
          Plans and credits
          <ArrowRight aria-hidden="true" className="size-3.5" />
        </Link>
      </>
    ),
  },
  {
    title: "Free accounts get a daily quota",
    body: "Free accounts use Haiku 5.5 up to a daily limit that resets at 00:00 UTC.",
  },
]

const questions = [
  {
    question: "Which models can I use?",
    answer:
      "Pro and Team start on Sonnet 5.5 and can switch to Haiku 5.5 from the model list. Free accounts use Haiku 5.5.",
  },
  {
    question: "What does a turn cost?",
    answer:
      "Each request is charged for the tokens it uses. A long session sends more context with every turn, so later turns cost more than early ones, and Opus costs more than Sonnet. Every charge is listed in your Billing history.",
  },
  {
    question: "How do I remove a device?",
    answer: (
      <>
        Every signed-in device is listed under Anysola Code at{" "}
        <a href={APPS_URL} className={inlineLink}>
          account.anysola.com/apps
          <ArrowUpRight aria-hidden="true" className="size-3.5" />
        </a>
        . Revoke it there. On a machine you still use,{" "}
        <code className="font-mono text-[13px] text-foreground">
          anysola logout
        </code>{" "}
        does the same.
      </>
    ),
  },
  {
    question: "Can I use it in CI?",
    answer: (
      <>
        Yes. Make a CI token under Anysola Code at{" "}
        <a href={APPS_URL} className={inlineLink}>
          account.anysola.com/apps
          <ArrowUpRight aria-hidden="true" className="size-3.5" />
        </a>{" "}
        and store it as the secret{" "}
        <code className="font-mono text-[13px] text-foreground">
          ANYSOLA_TOKEN
        </code>
        . On GitHub,{" "}
        <a href={ACTION_URL} className={inlineLink}>
          Anysola/code-action
          <ArrowUpRight aria-hidden="true" className="size-3.5" />
        </a>{" "}
        reviews pull requests and answers{" "}
        <code className="font-mono text-[13px] text-foreground">
          /anysola
        </code>{" "}
        on issues.
      </>
    ),
  },
  {
    question: "Is it open source?",
    answer: (
      <>
        Yes. Anysola Code is a fork of OpenCode, MIT licensed, at{" "}
        <a href={REPO_URL} className={inlineLink}>
          github.com/Anysola/code
          <ArrowUpRight aria-hidden="true" className="size-3.5" />
        </a>
        .
      </>
    ),
  },
]

function CodePage() {
  return (
    <SiteShell>
      <Container className="pt-10 pb-16 sm:pt-16 sm:pb-24">
        <TwoToneHeading
          as="h1"
          lead="Anysola Code"
          rest="A coding agent in your terminal."
          stacked
          className="text-[2.5rem] leading-[1.04] tracking-[-0.045em] sm:text-6xl"
        />

        <div className="mt-10 grid gap-3 sm:mt-14 lg:grid-cols-12">
          <div className="min-w-0 lg:col-span-7">
            <InstallBlock />
          </div>

          <section
            aria-labelledby="how-heading"
            className="rounded-surface bg-card p-2 lg:col-span-5"
          >
            <h2
              id="how-heading"
              className="px-3 pt-2 pb-3 text-sm font-medium text-foreground"
            >
              How it works
            </h2>
            <ol className="grid gap-0.5">
              {steps.map((step, i) => (
                <li
                  key={step.title}
                  className="flex gap-3 rounded-xl px-3 py-3"
                >
                  <span className="text-sm text-muted-foreground tabular-nums">
                    {i + 1}
                  </span>
                  <span className="min-w-0">
                    <span className="block text-[0.9375rem] text-foreground">
                      {step.title}
                    </span>
                    <span className="mt-1 block text-sm leading-relaxed text-muted-foreground">
                      {step.body}
                    </span>
                  </span>
                </li>
              ))}
            </ol>
          </section>
        </div>

        <section aria-labelledby="questions-heading" className="mt-16 sm:mt-24">
          <BlockHead id="questions-heading" title="Questions" />
          <div className="grid gap-0.5 rounded-surface bg-card p-2">
            {questions.map((item) => (
              <details
                key={item.question}
                className="group rounded-xl transition-colors open:bg-surface-secondary/60 hover:bg-surface-secondary/60"
              >
                <summary
                  className={cn(
                    summaryResetClass,
                    "flex items-center justify-between gap-4 rounded-xl px-3 py-3 text-[0.9375rem] text-foreground focus-visible:ring-2 focus-visible:ring-ring"
                  )}
                >
                  {item.question}
                  <Plus
                    aria-hidden
                    className="size-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-45"
                  />
                </summary>
                <p className="max-w-prose px-3 pb-4 text-sm leading-relaxed text-muted-foreground">
                  {item.answer}
                </p>
              </details>
            ))}
          </div>
        </section>

        <div className="mt-10 space-y-1 text-center text-xs text-muted-foreground">
          <p>Powered by Claude.</p>
          <p>
            Built on OpenCode (MIT). Not affiliated with Anthropic or the
            OpenCode project.
          </p>
        </div>
      </Container>
    </SiteShell>
  )
}

/**
 * Static: nothing here is read per visit. Until `CODE_PUBLIC`, /code is the
 * unknown-URL 404 of `$.tsx` (status, head and page), not a hidden page.
 */
export const Route = createFileRoute("/code")({
  loader: () => {
    if (!CODE_PUBLIC) respondNotFound()
  },
  head: () =>
    CODE_PUBLIC
      ? pageHead({
          title: "Code",
          description,
          path: "/code",
          image: "/code/opengraph-image",
        })
      : notFoundHead({ layout: false }),
  component: CODE_PUBLIC ? CodePage : PageNotFound,
})
